import React, { useCallback, useEffect, useState } from 'react';
import {
  bulkSubscribeToAuthorOrTopic,
  getNewslettersByIdArray,
  getUserNewsLetterCategories,
  subscribeToAuthorOrTopic,
  unsubscribeFromAuthorOrTopic,
} from '../services/NewsletterService';
import { Author, List, NewsletterResponse, Variant } from '../types/Newsletter';
import AuthorAndTopicCard from './AuthorAndTopicCard';
import AuthorAndTopicDialog from './AuthorAndTopicDialog';
import AuthorAndTopicSuggestion from './AuthorAndTopicSuggestion';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useTranslations } from '../../localization/useTranslations';
import Dialog from '../../common/components/Dialog';
import AuthorDetailsDialogContent from './AuthorDetailsDialogContent';

const AuthorAndTopicList: React.FC = () => {
  const [authors, setAuthors] = useState<Author[] | null>(null);
  const [topics, setTopics] = useState<Author[] | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [subscribedAuthors, setSubscribedAuthors] = useState<Author[] | null>(null);
  const [subscribedTopics, setSubscribedTopics] = useState<Author[] | null>(null);

  const [allTopics, setAllTopics] = useState<Author[] | null>(null);

  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const [openModalId, setOpenModalId] = useState<number | null>(null);
  const [dialogType, setDialogType] = useState<'author' | 'topic'>('author');

  const openAuthorModal = (id: number) => {
    setOpenModalId(id);
  };

  const closeAuthorModal = () => {
    setOpenModalId(null);
  };

  const translations = useTranslations();

  const sortTopicsList = useCallback((topics: any, topicsVariant: Variant[]) => {
    const sortedTopicsVariant = Array.isArray(topicsVariant)
      ? topicsVariant.sort((a, b) => a.sorting - b.sorting)
      : topicsVariant;

    const sortedTopics: any[] = [];

    sortedTopicsVariant.forEach((variant) => {
      const topic = topics.find((topic: any) => topic.id.toString() === variant.code);
      if (topic) sortedTopics.push(topic);
    });

    return sortedTopics;
  }, []);

  const getTopics = useCallback(
    async (response: NewsletterResponse, subscriptionListArray: List[]) => {
      const topics = response.lists.find((list) => list.code === 'topics')?.variants || [];
      const subscribedTopics = subscriptionListArray.find((list) => list.code === 'topics')?.variants || [];
      const topicsArray = Object.values(topics);
      const subscribedTopicsArray = Object.values(subscribedTopics);
      const topicIds = topicsArray.map((item) => item.code).join(',');

      const topicsListAll = await Promise.all([
        window.country === 'cz' ? getNewslettersByIdArray(topicIds, 'stitek') : Promise.resolve([]), // TODO: Remove this when stitek is removed
        getNewslettersByIdArray(topicIds, 'categories'),
      ]);

      const topicsList = topicsListAll.flat();
      const sortedTopicsList = sortTopicsList(topicsList, topicsArray);

      const subscribedTopicsList = sortedTopicsList.filter((topic: any) => {
        return subscribedTopicsArray.some((item) => item.code === topic.id.toString());
      });

      const notSubscribedTopicsList = sortedTopicsList.filter((topic: any) => {
        return !subscribedTopicsArray.some((item) => item.code === topic.id.toString());
      });

      setTopics(notSubscribedTopicsList);
      setSubscribedTopics(subscribedTopicsList);
      setAllTopics(sortedTopicsList);
      setIsLoading(false);
    },
    [sortTopicsList, setTopics, setSubscribedTopics, setIsLoading]
  );

  const sortAuthorList = useCallback((authors: Author[], authorVariant: Variant[]) => {
    const sortedAuthorVariant = Array.isArray(authorVariant)
      ? authorVariant.sort((a, b) => a.sorting - b.sorting)
      : authorVariant;

    const sortedAuthors: Author[] = [];

    sortedAuthorVariant.forEach((variant) => {
      const author = authors.find((author) => author.id.toString() === variant.code);
      if (author) sortedAuthors.push(author);
    });

    return sortedAuthors;
  }, []);

  const getAuthors = useCallback(
    async (response: NewsletterResponse, subscriptionListArray: List[]) => {
      const authors = response.lists.find((list) => list.code === 'authors')?.variants || [];

      const subscribedAuthors = subscriptionListArray.find((list) => list.code === 'authors')?.variants || [];

      const authorsArray = Object.values(authors);
      const subscribedAuthorsArray = Object.values(subscribedAuthors);
      const authorsList = await getNewslettersByIdArray(authorsArray.map((item) => item.code).join(','), 'coauthor');

      const sortedAuthorsList = sortAuthorList(authorsList, authorsArray);

      const subscribedAuthorsList = sortedAuthorsList.filter((author: any) => {
        return subscribedAuthorsArray.some((item) => item.code === author.id.toString());
      });

      const notSubscribedAuthorsList = sortedAuthorsList.filter((author: any) => {
        return !subscribedAuthorsArray.some((item) => item.code === author.id.toString());
      });

      setAuthors(notSubscribedAuthorsList);
      setSubscribedAuthors(subscribedAuthorsList);
    },
    [setAuthors, setSubscribedAuthors, sortAuthorList]
  );

  const getAuthorsAndTopics = useCallback(async () => {
    try {
      const response = window.NewsletterLists;
      const subscriptionsList = await getUserNewsLetterCategories();
      const subscriptionListArray: List[] = Object.values(subscriptionsList.subscriptions);

      getAuthors(response, subscriptionListArray);
      getTopics(response, subscriptionListArray);
    } catch (error) {
      console.error(error);
    }
  }, [getAuthors, getTopics]);

  useEffect(() => {
    getAuthorsAndTopics();
  }, [getAuthorsAndTopics]);

  const handleAuthorDialogOpen = () => {
    setIsSearchModalOpen(true);
    setDialogType('author');
    setTimeout(() => {
      document.documentElement.classList.add('no-scroll');
    }, 100);
  };

  const onFollowClick = (id: number, type: 'author' | 'topic') => {
    if (type === 'author') followAuthor(id);
    else if (type === 'topic') followTopic(id);
  };

  const openDialog = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const followAuthor = async (id: number) => {
    const author = authors?.find((item) => item.id === id);
    if (author) {
      if (!authors) return;
      try {
        const response = await subscribeToAuthorOrTopic('authors', author.id);

        if (response.status !== 'ok') {
          openDialog(translations?.snackBarMessages?.failedSubscribeAuthor || 'Error subscribing to author');
        } else {
          setAuthors(authors.filter((item) => item.id !== id));
          setSubscribedAuthors([...(subscribedAuthors || []), author]);
          openDialog(translations?.snackBarMessages?.subscribedAuthor || 'Subscribed to author');
        }
      } catch (error) {
        openDialog(translations?.snackBarMessages?.failedSubscribeAuthor || 'Error subscribing to author');
        console.error(error);
      }
    } else {
      const subscribedAuthor = subscribedAuthors?.find((item) => item.id === id);

      if (!subscribedAuthor || !subscribedAuthors) return;
      try {
        const response = await unsubscribeFromAuthorOrTopic('authors', subscribedAuthor.id);

        if (response.status !== 'ok') {
          openDialog(translations?.snackBarMessages?.failedUnsubscribeAuthor || 'Error unsubscribing from author');
        } else {
          setSubscribedAuthors(subscribedAuthors.filter((item) => item.id !== id));
          setAuthors([...(authors || []), subscribedAuthor]);
          openDialog(translations?.snackBarMessages?.unsubscribedAuthor || 'Unsubscribed from author');
        }
      } catch (error) {
        openDialog(translations?.snackBarMessages?.failedUnsubscribeAuthor || 'Error unsubscribing from author');
        console.error(error);
      }
    }
  };

  const followTopic = async (id: number) => {
    const topic = topics?.find((item) => item.id === id);

    if (topic) {
      if (!topics) return;

      try {
        const response = await subscribeToAuthorOrTopic('topics', topic.id);

        if (response.status !== 'ok') {
          openDialog(translations?.snackBarMessages?.failedSubscribeTopic || 'Error subscribing to topic');
        } else {
          setTopics(topics.filter((item) => item.id !== id));

          setSubscribedTopics([...(subscribedTopics || []), topic]);

          openDialog(translations?.snackBarMessages?.subscribedTopic || 'Subscribed to topic');
        }
      } catch (error) {
        console.error(error);

        openDialog(translations?.snackBarMessages?.failedSubscribeTopic || 'Error subscribing to topic');
      }
    } else {
      const topic = subscribedTopics?.find((item) => item.id === id);

      if (!subscribedTopics || !topic) return;

      try {
        const response = await unsubscribeFromAuthorOrTopic('topics', topic.id);

        if (response.status !== 'ok') {
          openDialog(translations?.snackBarMessages?.failedUnsubscribeTopic || 'Error unsubscribing from topic');
        } else {
          setSubscribedTopics(subscribedTopics.filter((item) => item.id !== id));

          setTopics([...(topics || []), topic]);

          openDialog(translations?.snackBarMessages?.unsubscribedTopic || 'Unsubscribed from topic');
        }
      } catch (error) {
        console.error(error);

        openDialog(translations?.snackBarMessages?.failedUnsubscribeTopic || 'Error unsubscribing from topic');
      }
    }
  };

  const followAllSuggested = async (type: 'author' | 'topic') => {
    if (type === 'author') {
      const authorsToFollow = authors?.slice(0, 3);
      if (!authorsToFollow) return;

      try {
        const response = await bulkSubscribeToAuthorOrTopic(
          'authors',
          authorsToFollow.map((author) => author.id)
        );

        if (response.status !== 'ok') {
          openDialog(translations?.snackBarMessages?.failedSubscribeAuthor || 'Error subscribing to author');
        } else {
          if (!authors) return;

          setAuthors(authors?.filter((item) => !authorsToFollow.some((author) => author.id === item.id)));

          setSubscribedAuthors([...(subscribedAuthors || []), ...authorsToFollow]);
          openDialog(translations?.snackBarMessages?.subscribedAuthor || 'Subscribed to authors');
        }
      } catch (error) {
        console.error(error);
        openDialog(translations?.snackBarMessages?.failedSubscribeAuthor || 'Error subscribing to author');
      }
    } else if (type === 'topic') {
      const topicsToFollow = topics?.slice(0, 3);
      if (!topicsToFollow) return;

      try {
        const response = await bulkSubscribeToAuthorOrTopic(
          'topics',
          topicsToFollow.map((topic) => topic.id)
        );

        if (response.status !== 'ok') {
          openDialog(translations?.snackBarMessages?.failedSubscribeTopic || 'Error subscribing to topic');
        } else {
          if (!topics) return;

          setTopics(topics?.filter((item) => !topicsToFollow.some((topic) => topic.id === item.id)));

          setSubscribedTopics([...(subscribedTopics || []), ...topicsToFollow]);
          openDialog(translations?.snackBarMessages?.subscribedTopic || 'Subscribed to topics');
        }
      } catch (error) {
        console.error(error);
        openDialog(translations?.snackBarMessages?.failedSubscribeTopic || 'Error subscribing to topic');
      }
    }
  };

  const onCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Todo: Add styling to loading state
  // Note: If it is not needed just comment out
  if (isLoading) {
    return (
      <main className="saved-articles" style={{ height: '100vh' }}>
        <div className="container" />
      </main>
    );
  }

  return (
    <>
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => onCloseSnackbar()} />
      <main className="followed-content">
        <div className="container">
          <div className="followed-content__inner-container">
            <div
              className={`followed-content__section ${
                subscribedAuthors && subscribedAuthors.length > 0 ? '' : 'empty'
              }`}
            >
              <h4 className="followed-content__title">{translations?.authorAndTopic?.authors || 'Authors'}</h4>

              {subscribedAuthors && subscribedAuthors.length === 0 ? (
                <AuthorAndTopicSuggestion
                  suggestedArticlesOrTopics={authors?.slice(0, 3) || []}
                  type="author"
                  text={translations?.authorAndTopic?.authorText || 'Follow authors'}
                  followAllButtonText={
                    `${translations?.authorAndTopic?.followAll} (${authors?.slice(0, 3).length})` || 'Follow all'
                  }
                  dialogOpenButtonText={translations?.authorAndTopic?.searchAuthor || 'Search authors'}
                  onFollowClick={(id: number, type: 'author' | 'topic') => onFollowClick(id, type)}
                  handleAuthorDialogOpen={handleAuthorDialogOpen}
                  followAllSuggested={() => followAllSuggested('author')}
                  openAuthorModal={openAuthorModal}
                  openModalId={openModalId}
                  closeAuthorModal={closeAuthorModal}
                />
              ) : (
                <div className="followed-content__section-wrapper">
                  <div className="followed-content__grid followed-content__grid--authors">
                    {subscribedAuthors &&
                      subscribedAuthors.map((author: Author) => (
                        <React.Fragment key={author.id}>
                          <AuthorAndTopicCard
                            title={author.name}
                            id={author.id.toString()}
                            type="author"
                            isFollowed={true}
                            imageUrl={author.acf?.profileImageUrl ?? ''}
                            name={author.name ?? ''}
                            description={author.description ?? ''}
                            shortDescription={author.acf?.shortDescription ?? ''}
                            link={author.link}
                            onFollowClick={(id: number, type: 'author' | 'topic') => onFollowClick(id, type)}
                            layout={'followed-authors'}
                            isFollowable={true}
                            linkedin={author.acf?.linkedin_url ?? ''}
                            instagram={author.acf?.instagram_url ?? ''}
                            twitter={author.acf?.twitter_url ?? ''}
                            email={author.acf?.author_email ?? ''}
                            nameType="button" // it should open a modal when clicked
                            openAuthorModal={() => openAuthorModal(author.id)}
                          />
                          <Dialog isOpen={openModalId === author.id} onClose={closeAuthorModal}>
                            <AuthorDetailsDialogContent
                              id={author.id.toString()}
                              authorName={author.name}
                              authorImageUrl={author.acf?.profileImageUrl ?? ''}
                              authorTitle={author.name}
                              authorDescription={author.description}
                              authorLink={author.link}
                              onFollowClick={(id: number, type: 'author' | 'topic') => onFollowClick(id, type)}
                              isFollowed={true}
                              hasSocialLinks
                              linkedin={author.acf?.linkedin_url ?? ''}
                              instagram={author.acf?.instagram_url ?? ''}
                              twitter={author.acf?.twitter_url ?? ''}
                              email={author.acf?.author_email ?? ''}
                              isFollowable={true}
                              onClose={closeAuthorModal}
                            />
                          </Dialog>
                        </React.Fragment>
                      ))}

                    {/* Find more card */}
                    {subscribedAuthors && subscribedAuthors.length > 0 && (
                      <div id="find-more-authors" className="find-more-card" onClick={() => handleAuthorDialogOpen()}>
                        <div className="find-more-card__icon-wrapper">
                          <div className="find-more-card__icon" />
                        </div>

                        <h4 className="find-more-card__title link-underline-16">
                          {translations?.authorAndTopic?.findMoreAuthors || 'Find more authors'}
                        </h4>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="followed-content__section followed-content__section--topics">
              <h4 className="followed-content__title">{translations?.authorAndTopic?.topics || 'Topics'}</h4>

              {subscribedTopics?.length === 0 && (
                <p className="basic-16">{translations?.authorAndTopic?.topicText || 'Follow authors'}</p>
              )}

              <div className="">
                {allTopics?.map((author: Author) => (
                  <AuthorAndTopicCard
                    key={author.id}
                    title={author.name}
                    id={author.id.toString()}
                    type="topic"
                    link={author.link}
                    imageUrl={author.acf?.newsletter_image ?? ''}
                    shortDescription={author.acf?.newsletter_description ?? ''}
                    isFollowed={subscribedTopics?.includes(author) ?? false}
                    onFollowClick={(id: number, type: 'author' | 'topic') => onFollowClick(id, type)}
                    layout={'full-width'}
                    isFollowable={true}
                    nameType="link" // it should redirect to topic page when clicked
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <AuthorAndTopicDialog
          authorOrTopic={dialogType === 'author' ? authors : topics}
          type={dialogType}
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onFollowClick={(id: number, type: 'author' | 'topic') => onFollowClick(id, type)}
        />
      </main>
    </>
  );
};

export default AuthorAndTopicList;
